import { ChevronDown, ChevronUp, Search, Users } from "lucide-react"
import { useCallback, useEffect } from "react"

import type { LabeledSelectOption } from "@/components/ui/labeled-select"
import type { MissingTalentSummary } from "@/lib/talent-tracking-api"

import { Button } from "@/components/button"
import { CardHeading } from "@/components/shared/card-heading"
import { Tag } from "@/components/shared/tag"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Input } from "@/components/ui/input"
import { LabeledSelect } from "@/components/ui/labeled-select"
import {
  useMemberSearchInput,
  type TalentOverviewUiStore,
} from "@/lib/stores/talent-overview-ui-store"
import { cn } from "@/lib/utils"

import { CompactMatrixView } from "./compact-matrix-view"
import { MEMBER_QUERY_DEBOUNCE_MS } from "./constants"
import { TalentCardsView } from "./talent-cards-view"

const CATEGORY_OPTIONS: readonly LabeledSelectOption<
  "offensive" | "defensive" | "strategic" | "all"
>[] = [
  { value: "all", label: "All categories" },
  { value: "offensive", label: "Offensive" },
  { value: "defensive", label: "Defensive" },
  { value: "strategic", label: "Strategic" },
]

const TALENT_SORT_OPTIONS: readonly LabeledSelectOption<
  "most-missing" | "tier" | "name"
>[] = [
  { value: "most-missing", label: "Most missing first" },
  { value: "tier", label: "Tier ascending" },
  { value: "name", label: "Name A-Z" },
]

type MatrixRow = {
  userId: string
  displayName: string | null
  missingCount: number
  completedCount: number
}

type SeasonBoss = {
  id: string
  name: string
  season: string
  missingCount: number
  talents: MissingTalentSummary[]
}

export function BossDetailPanel({
  store,
  selectedBoss,
  overviewMode,
  showOnlyMissing,
  selectedCategory,
  talentSortMode,
  cardSortMode,
  isAdvancedFiltersOpen,
  visibleTalents,
  matrixRows,
  matrixTalents,
  memberQuery,
  totalMembers,
  onUpdateSearch,
}: {
  store: TalentOverviewUiStore
  selectedBoss: SeasonBoss
  overviewMode: string
  showOnlyMissing: boolean
  selectedCategory: string
  talentSortMode: string
  cardSortMode: string
  isAdvancedFiltersOpen: boolean
  visibleTalents: MissingTalentSummary[]
  matrixRows: MatrixRow[]
  matrixTalents: MissingTalentSummary[]
  memberQuery: string
  totalMembers: number
  onUpdateSearch: (
    updates: Record<string, string | boolean | undefined>,
    options?: { replace?: boolean },
  ) => void
}) {
  const memberSearchInput = useMemberSearchInput(store)
  const { setMemberSearchInput } = store.getState()

  // Sync member search input from URL
  useEffect(() => {
    setMemberSearchInput(memberQuery)
  }, [memberQuery, setMemberSearchInput])

  // Debounce member search input → URL
  const syncSearch = useCallback(() => {
    if (overviewMode !== "matrix") {
      return
    }

    const nextValue = memberSearchInput.trim()
    const currentValue = memberQuery.trim()

    if (nextValue === currentValue) {
      return
    }

    const timeout = globalThis.setTimeout(() => {
      onUpdateSearch({
        memberQuery: nextValue.length > 0 ? nextValue : undefined,
      })
    }, MEMBER_QUERY_DEBOUNCE_MS)

    return () => {
      globalThis.clearTimeout(timeout)
    }
  }, [memberQuery, memberSearchInput, overviewMode, onUpdateSearch])

  useEffect(syncSearch, [syncSearch])

  return (
    <Card>
      <CardHeader>
        <CardHeading icon={<Users className="size-5 text-primary" />}>
          <span className="truncate">{selectedBoss.name}</span>
        </CardHeading>
        <CardDescription>
          {selectedBoss.missingCount} members are missing at least one tracked
          talent for this boss.
        </CardDescription>
        <div className="inline-card mt-1 grid gap-3 bg-card/40">
          <div className="flex flex-wrap items-center gap-2">
            {(
              [
                ["cards", "Card View"],
                ["matrix", "Matrix View"],
              ] as const
            ).map(([mode, label]) => (
              <Button
                unstyled
                key={mode}
                type="button"
                onClick={() => {
                  onUpdateSearch({
                    mode,
                    memberQuery: mode === "cards" ? undefined : memberQuery,
                  })
                }}
                className={cn(
                  "rounded-xl border px-3 py-2 text-sm font-semibold transition",
                  overviewMode === mode
                    ? "border-primary/25 bg-primary/10 text-primary"
                    : "border-border/60 bg-card/65 text-foreground hover:border-primary/15",
                )}
              >
                {label}
              </Button>
            ))}
            <Button
              unstyled
              type="button"
              onClick={() => onUpdateSearch({ onlyMissing: !showOnlyMissing })}
              className={cn(
                "rounded-xl border px-3 py-2 text-sm font-semibold transition",
                showOnlyMissing
                  ? "border-primary/25 bg-primary/10 text-primary"
                  : "border-border/60 bg-card/65 text-foreground hover:border-primary/15",
              )}
            >
              {showOnlyMissing
                ? "Only Members Missing Something"
                : "Show All Members"}
            </Button>
            <Tag>{visibleTalents.length} talents shown</Tag>
            <Tag>{matrixRows.length} members match</Tag>
          </div>

          <Collapsible
            open={isAdvancedFiltersOpen}
            onOpenChange={(open) => onUpdateSearch({ advancedFilters: open })}
            className="grid gap-3"
          >
            <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
              <div className="grid gap-1.5">
                <span className="text-kicker font-semibold text-muted-foreground">
                  Category
                </span>
                <LabeledSelect
                  value={selectedCategory}
                  onValueChange={(value) => onUpdateSearch({ category: value })}
                  options={CATEGORY_OPTIONS}
                  className="w-full"
                />
              </div>
              <div className="flex items-end justify-start lg:justify-end">
                <CollapsibleTrigger className="inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-card/70 px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-card hover:text-foreground">
                  {isAdvancedFiltersOpen ? "Hide filters" : "More filters"}
                  {isAdvancedFiltersOpen ? (
                    <ChevronUp className="size-3.5" />
                  ) : (
                    <ChevronDown className="size-3.5" />
                  )}
                </CollapsibleTrigger>
              </div>
            </div>

            <CollapsibleContent>
              <div className="grid gap-3 lg:grid-cols-3">
                <div className="grid gap-1.5">
                  <span className="text-kicker font-semibold text-muted-foreground">
                    Talent sort
                  </span>
                  <LabeledSelect
                    value={talentSortMode}
                    onValueChange={(value) =>
                      onUpdateSearch({ talentSort: value })
                    }
                    options={TALENT_SORT_OPTIONS}
                    className="w-full"
                  />
                </div>

                {overviewMode === "matrix" ? (
                  <div className="grid gap-1.5 lg:col-span-2">
                    <span className="text-kicker font-semibold text-muted-foreground">
                      Member search
                    </span>
                    <div className="relative">
                      <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={memberSearchInput}
                        onChange={(event) => {
                          setMemberSearchInput(event.target.value)
                        }}
                        placeholder="Filter by display name"
                        className="h-10 pl-9"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="hidden lg:block" />
                )}

                {overviewMode === "cards" ? (
                  <div className="grid gap-1.5 lg:col-span-3">
                    <span className="text-kicker font-semibold text-muted-foreground">
                      Card sort
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {(
                        [
                          ["severity", "Severity first"],
                          ["tier", "Tier ascending"],
                        ] as const
                      ).map(([mode, label]) => (
                        <Button
                          unstyled
                          key={mode}
                          type="button"
                          onClick={() => onUpdateSearch({ cardSort: mode })}
                          className={cn(
                            "rounded-xl border px-3 py-2 text-sm font-semibold transition",
                            cardSortMode === mode
                              ? "border-primary/25 bg-primary/10 text-primary"
                              : "border-border/60 bg-card/65 text-foreground hover:border-primary/15",
                          )}
                        >
                          {label}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </CardHeader>
      <CardContent>
        {overviewMode === "cards" ? (
          <TalentCardsView
            visibleTalents={visibleTalents}
            selectedCategory={selectedCategory}
            cardSortMode={cardSortMode}
            totalMembers={totalMembers}
          />
        ) : (
          <CompactMatrixView rows={matrixRows} talents={matrixTalents} />
        )}
      </CardContent>
    </Card>
  )
}
